import { RootState } from '@/types/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { EmailsManagementService } from '@/services/emails-management.service';

export interface IEmailTemplate {
  id: string;
  name: string;
  alias: string | null;
  status: string;
  updatedAt: string;
}

export interface IEmailTemplateVariable {
  key: string;
  type: string;
  fallbackValue: string | number | null;
}

export interface IEmailTemplateDetail extends IEmailTemplate {
  subject: string | null;
  from: string | null;
  variables: IEmailTemplateVariable[];
  html?: string;
}

export interface IEmailRecipient {
  email: string;
  name?: string;
  variables?: Record<string, string | number>;
}

export interface ISendTemplateEmailPayload {
  recipients: IEmailRecipient[];
  templateId: string;
  subject?: string;
  from?: string;
}

interface EmailsManagementState {
  templates: IEmailTemplate[];
  templateDetail: IEmailTemplateDetail | null;
  lastSendResult: { queued: number; failed: Array<{ email: string; reason: string }> } | null;
}

const state: EmailsManagementState = {
  templates: [],
  templateDetail: null,
  lastSendResult: null,
};

const mutations: MutationTree<EmailsManagementState> = {
  UPDATE_TEMPLATES(state, payload: IEmailTemplate[]) {
    state.templates = payload;
  },
  UPDATE_TEMPLATE_DETAIL(state, payload: IEmailTemplateDetail | null) {
    state.templateDetail = payload;
  },
  UPDATE_LAST_SEND_RESULT(state, payload: EmailsManagementState['lastSendResult']) {
    state.lastSendResult = payload;
  },
};

const actions: ActionTree<EmailsManagementState, RootState> = {
  async listTemplates({ commit, dispatch }) {
    try {
      const response$ = await EmailsManagementService.listTemplates();
      commit('UPDATE_TEMPLATES', response$.data?.templates ?? []);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
    }
  },
  async templateDetail({ commit, dispatch }, id: string) {
    try {
      dispatch('isDialogLoading', true, { root: true });
      commit('UPDATE_TEMPLATE_DETAIL', null);
      const response$ = await EmailsManagementService.getTemplate(id);
      commit('UPDATE_TEMPLATE_DETAIL', response$.data ?? null);
      dispatch('isDialogLoading', false, { root: true });
    } catch (e) {
      dispatch('isDialogLoading', false, { root: true });
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
    }
  },
  async sendTemplate({ commit, dispatch }, payload: ISendTemplateEmailPayload) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await EmailsManagementService.sendTemplate(payload);
      const result = response$.data as EmailsManagementState['lastSendResult'];
      commit('UPDATE_LAST_SEND_RESULT', result);
      dispatch('isLoading', false, { root: true });
      dispatch('updateDialog', { idx: 'sendEmail', state: false }, { root: true });
      dispatch('resetFormValues', true, { root: true });
      const failedCount = result?.failed?.length ?? 0;
      dispatch(
        'snackBarMessage',
        failedCount > 0
          ? `${result?.queued ?? 0} email(s) queued, ${failedCount} failed`
          : `${result?.queued ?? 0} email(s) queued successfully`,
        { root: true },
      );
      dispatch('snackBarVisibility', true, { root: true });
      return true;
    } catch (e) {
      dispatch('isLoading', false, { root: true });
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      return false;
    }
  },
};

const getters: GetterTree<EmailsManagementState, RootState> = {
  getTemplates: state => state.templates,
  getTemplateDetail: state => state.templateDetail,
  getLastSendResult: state => state.lastSendResult,
};

export const emailsManagement: Module<EmailsManagementState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
